/**
 * 署名付きリンクのトークン（REC-443）。
 *
 * 管理システムは LINE で配る URL に、注文に紐づくトークンをクエリの `t` で載せる
 * （`?s={店舗}&o={注文}&t={トークン}`）。フォームはそれを `/api/call` の本文に
 * **同じ名前の `t`** で付けて送る。管理システムはトークンが指す注文とリクエストの
 * `order_id` を突き合わせる（ug-manage-system の `VerifySignedLink`）。
 *
 * ヘッダーで送らないのは、管理システムの nginx が CORS で許しているヘッダーが
 * `Content-Type` と `Authorization` だけだから。URL のクエリで送らないのは、
 * nginx のアクセスログに残るから（本文は残らない）。
 *
 * **トークンは URL に残さない。** 読んだらすぐ消す。残すとアドレスバーから
 * URL ごと共有・コピーされ、画面の中から出す通信の Referer にも載る。
 * （開いた瞬間の URL がブラウザの閲覧履歴に記録されるのは、画面からは防げない）
 * 消すと開き直したときに失われるので、同じブラウザのあいだ localStorage に持つ
 * （REC-643）。sessionStorage だと同じタブでしか残らず、同じブラウザの別タブで開く・
 * 履歴やブックマークから開く・閉じたタブを復元する、のどれでもトークン無しの送信に
 * なっていた。**localStorage はブラウザごとに別**なので、LINE の中のブラウザから
 * Safari で開き直したときは引き継がれない（それは URL で渡す別の仕組みが要る。別件）。
 *
 * **トークンが無くても送れる形にしておく。** 管理システムが記録モードのあいだは
 * トークンが無くても通る。既に配った古いリンクにはトークンが無い。
 */

export const QUERY_KEY = "t";

/* 受け取ったトークンを残す場所（localStorage のキー）。
   sessionStorage に持っていたころ（REC-443〜REC-573）と同じ名前。下の移行で読む */
const STORAGE_KEY = "ug-castcall-form.signed-link";

/* **受け取ってから 72 時間で捨てる。** 管理システムがリンクを受け付ける期限
   （`SIGNED_LINK_TTL_CALL` の既定）と同じ長さ。期限を過ぎたトークンは送っても
   断られるだけなので、同じ長さで手元からも消す。localStorage はタブを閉じても
   残るので、期限が無いと受け付けられないトークンが溜まり続ける */
export const TOKEN_TTL_MS = 72 * 60 * 60 * 1000;

/* **注文ごとに、直近の数件を残す。** 「コール画面へ移動」で前の受付を開いてから
   戻ると、元の受付は t の消えた URL で読み込み直しになることがある。最後の 1 件だけだと、
   そこで元の受付のトークンが失われる（REC-573）。行き来するのは 2 件なので、数件で足りる */
const STORAGE_LIMIT = 5;

let current = null;

/* vue-router と同じ読み方（`+` は空白、デコードできなければそのまま）。
   注文の照合で、ルーターが読んだ `o` と食い違わないようにする */
function decode(value) {
  const text = value.replace(/\+/g, " ");
  try {
    return decodeURIComponent(text);
  } catch (e) {
    return text;
  }
}

function parse(search) {
  const query = search.replace(/^\?/, "");
  if (!query) {
    return [];
  }

  return query.split("&").map((raw) => {
    const at = raw.indexOf("=");
    const key = decode(at === -1 ? raw : raw.slice(0, at));
    const value = at === -1 ? "" : decode(raw.slice(at + 1));

    return { raw, key, value };
  });
}

/* window の Storage を取り出す。**取り出すだけで例外になる環境がある**
   （Cookie をすべて拒否した Safari など）ので、無いときと同じ扱いにする */
function storageOf(win, name) {
  try {
    return win[name] || null;
  } catch (e) {
    return null;
  }
}

/* Storage に残してあるリンクの一覧（新しい順）。読めない・壊れている・期限切れは捨てる。
   `expiresAt` の無いもの（いつ受け取ったか分からない）は `fallbackExpiresAt` を当てる */
function readLinks(storage, now, fallbackExpiresAt) {
  if (!storage) {
    return [];
  }

  let stored = null;
  try {
    stored = JSON.parse(storage.getItem(STORAGE_KEY) || "null");
  } catch (e) {
    /* 読めなければ、トークンが無いときと同じ扱い */
    return [];
  }

  /* 1 件だけ持っていたころの形（`{orderId, token}`）も読む */
  const links = Array.isArray(stored) ? stored : [stored];

  return links
    .filter(
      (link) => link && typeof link.token === "string" && link.token !== ""
    )
    .map((link) => ({
      orderId: String(link.orderId || ""),
      token: link.token,
      expiresAt:
        typeof link.expiresAt === "number" ? link.expiresAt : fallbackExpiresAt,
    }))
    .filter((link) => link.expiresAt > now);
}

function writeLinks(storage, links) {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(links));
    return true;
  } catch (e) {
    /* 使えない端末（プライベートモードなど）では、開き直したときだけ
       トークン無しになる。リンクを開き直せば元に戻るので、ここでは何もしない */
    return false;
  }
}

/* localStorage のうち、まだ期限内のもの。`expiresAt` が無いものは捨てる
   （localStorage には必ず付けて書く。無いのはいつ受け取ったか分からない） */
function loadAll(win, now) {
  return readLinks(storageOf(win, "localStorage"), now, 0);
}

/**
 * sessionStorage に持っていたころ（REC-573 まで）のトークンを localStorage へ写す。
 *
 * 配信が入れ替わった直後に、開いていたタブを読み込み直してもトークンを失わないように。
 * いつ受け取ったか分からないので、いまから 72 時間を期限にする（古すぎれば管理システムが
 * 断るだけ）。写せたら sessionStorage から消し、**1 回だけ**にする。localStorage へ
 * 書けなかったときは残しておき、次に開いたときにもう一度試す。
 *
 * 読めた分（sessionStorage にあったもの）を返す。写せなかったときに、このタブでは
 * メモリ上でそれを使うため。
 */
function migrateFromSession(win, now, orderId) {
  const session = storageOf(win, "sessionStorage");
  const legacy = readLinks(session, now, now + TOKEN_TTL_MS);
  if (legacy.length === 0) {
    return legacy;
  }

  /* 同じ注文は localStorage の方（新しい）を残す。**いま開いている注文は上限より優先する**。
     localStorage が既に 5 件のとき、後ろに足して切ると、開いている注文の分だけが落ちて
     元の sessionStorage からも消えてしまう */
  const kept = loadAll(win, now);
  const added = legacy.filter(
    (link) => !kept.some((saved) => saved.orderId === link.orderId)
  );
  const opening = added.filter((link) => link.orderId === orderId);
  const links = opening
    .concat(kept)
    .concat(added.filter((link) => link.orderId !== orderId))
    .slice(0, STORAGE_LIMIT);

  if (writeLinks(storageOf(win, "localStorage"), links)) {
    try {
      session.removeItem(STORAGE_KEY);
    } catch (e) {
      /* 消せなくても次に読んだときに localStorage の方が勝つだけ */
    }
  }

  return legacy;
}

/* 同じ注文は新しい方で置き換える。前のリンクは管理システム側で失効させないので、
   どちらを送っても通るが、期限が延びる新しい方を残す */
function save(win, link, now) {
  const links = [link]
    .concat(loadAll(win, now).filter((saved) => saved.orderId !== link.orderId))
    .slice(0, STORAGE_LIMIT);

  writeLinks(storageOf(win, "localStorage"), links);
}

function load(win, orderId, now) {
  return loadAll(win, now).find((link) => link.orderId === orderId) || null;
}

/**
 * 開いた URL からトークンを抜き取り、URL から消す。
 *
 * **ルーターが URL を読む前（`new Vue` より前）に呼ぶ。** 後から消すと、
 * ルートの `query` にトークンが残る。
 *
 * 消すのは `t` だけで、`s` や `o` は書き換えない（元の文字列のまま残す）。
 *
 * Storage が使えない環境でも例外で止まらない。開いた画面のあいだはメモリ上の
 * `current` で動く（開き直したときだけトークン無しになる）。
 */
export function captureFromLocation(win) {
  const now = Date.now();
  const params = parse(win.location.search || "");
  const orderParam = params.find((param) => param.key === "o");
  const orderId = orderParam ? orderParam.value : "";
  const tokens = params.filter((param) => param.key === QUERY_KEY);
  const legacy = migrateFromSession(win, now, orderId);

  if (tokens.length === 0) {
    /* トークンの無い URL。開き直し（同じブラウザの別のタブ・履歴・ブックマーク・
       タブの復元を含む）なら、先に読んで残してあるものを使う。localStorage へ写せずに
       sessionStorage に残ったまま（プライベートモードなど）のものも、このタブでは使う */
    current =
      load(win, orderId, now) ||
      legacy.find((link) => link.orderId === orderId) ||
      null;
    return;
  }

  const rest = params
    .filter((param) => param.key !== QUERY_KEY)
    .map((param) => param.raw)
    .join("&");
  win.history.replaceState(
    win.history.state,
    "",
    win.location.pathname + (rest ? "?" + rest : "") + (win.location.hash || "")
  );

  const token = tokens[0].value;
  current = token ? { orderId, token, expiresAt: now + TOKEN_TTL_MS } : null;
  if (current) {
    save(win, current, now);
  }
}

/**
 * その注文に使うトークン。無ければ空文字。
 *
 * **リンクの注文と違う注文には付けない。** 管理システムは注文が食い違うトークンを
 * 断る（記録モードでも「突き合わせの失敗」として記録に残る）。
 */
export function tokenFor(orderId) {
  if (!current || orderId === undefined || orderId === null) {
    return "";
  }

  return String(orderId) === current.orderId ? current.token : "";
}

/**
 * `/api/call` へ送る本文にトークンを足す。トークンが無ければ本文はそのまま
 * （今までと同じリクエストになる）。
 */
export function withToken(body, orderId) {
  const token = tokenFor(orderId);
  if (!token) {
    return body;
  }

  return Object.assign({}, body, { [QUERY_KEY]: token });
}

/**
 * 別の受付のフォームを開く URL（「コール画面へ移動」。REC-573）。
 *
 * 前の受付は別の注文なので、開いているリンクのトークンは使えない。管理システムが
 * post_type 5 の応答で渡すその受付用のトークン（`incall_item_token`）を `t` に載せる。
 * **渡されなかったら付けない**（トークン無しで開いている呼び出しや、発行に
 * 失敗したときは空で来る）。そのときは今までと同じ URL になる。
 *
 * 開いた先では `captureFromLocation()` が `t` を読み取り、URL から消す。
 */
export function orderFormUrl(base, storeId, orderId, token) {
  const query = [
    "s=" + encodeURIComponent(String(storeId)),
    "o=" + encodeURIComponent(String(orderId)),
  ];
  if (typeof token === "string" && token !== "") {
    query.push(QUERY_KEY + "=" + encodeURIComponent(token));
  }

  return base + "?" + query.join("&");
}

/**
 * 管理システムがリンクを断ったか。
 *
 * 管理システムは、トークンが無い・壊れている・期限切れ・失効・注文の食い違いの
 * **どの理由でも同じ 404** を返す（理由を出し分けると、注文の有無が外から分かるため）。
 * いまは返さない 401・403 も同じ扱いにしておく（断り方が変わっても案内が出るように）。
 *
 * 応答が無い（`Network Error`）ときは false。管理システムの nginx の CORS ヘッダーに
 * `always` が無いと、断った応答もブラウザが読めずここに来る。
 */
export function isLinkRefused(error) {
  const status = error && error.response ? error.response.status : 0;

  return status === 401 || status === 403 || status === 404;
}

/**
 * 応答が届かなかったか（電波・タイムアウト、または CORS で中身を読めなかった）。
 * 画面の処理中に起きた例外（`isAxiosError` が無い）は含めない。
 */
export function isNetworkError(error) {
  return Boolean(error && error.isAxiosError && !error.response);
}

/**
 * console に出してよい形にする。
 *
 * **axios のエラーをそのまま console に出さない。** `error.config.data` に
 * 送った本文（トークンを含む）が入っている。
 */
export function describeError(error) {
  if (error && error.response) {
    return "status " + error.response.status;
  }

  return (error && error.message) || "unknown error";
}
